import { useMutation, useQueryClient } from 'react-query'
import { useSnackbar } from 'notistack'
import {
  updateCustomerGroup,
  UpdateCustomerGroupArgs,
} from 'services/cm-api/update-customer-group'
import { WebApi2Response, CustomerGroupT } from 'types/global'

/**
 * Wraps `PUT /customer-group/:id`. Identical error/success handling
 * to the create mutation — the CMAPI's uniqueness rule applies to
 * patches the same way it does to inserts.
 */
export function useUpdateCustomerGroup() {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    (args: UpdateCustomerGroupArgs) => updateCustomerGroup(args),
    {
      onSuccess: async (response: WebApi2Response<CustomerGroupT>) => {
        if (response.success) {
          enqueueSnackbar(`Customer group "${response.data.name}" updated`, {
            variant: 'success',
          })
          await queryClient.invalidateQueries(['getCustomerGroups'])
        }
      },
      onError: (err: unknown) => {
        const axiosErr = err as {
          response?: { data?: { error?: { message?: string } } }
        }
        const apiMessage = axiosErr?.response?.data?.error?.message
        enqueueSnackbar(apiMessage ?? 'Failed to update customer group', {
          variant: 'error',
        })
        console.error('updateCustomerGroup failed: ', err)
      },
    },
  )
}
